import { useState, useEffect } from 'react';
import { ShoppingCart, TrendingUp, Package } from 'lucide-react';
import cartAdminService from '@services/admin/cart.admin.service';

const ManageCarts = () => {
  const [statistics, setStatistics] = useState({
    totalCartAdditions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await cartAdminService.getCartStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch cart statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <ShoppingCart className="w-7 h-7 text-[#c33c3d]" />
          <h1 className="text-2xl font-bold text-gray-900">Manage Carts</h1>
        </div>
        <p className="text-gray-600 text-sm">
          Track student interest in educational materials through cart additions
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Cart Additions */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-500 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-blue-700">Total Cart Additions</p>
              {loading ? (
                <div className="h-10 w-20 bg-blue-200 animate-pulse rounded mt-1"></div>
              ) : (
                <h2 className="text-4xl font-bold text-blue-900 mt-1">
                  {statistics.totalCartAdditions}
                </h2>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <TrendingUp className="w-4 h-4" />
            <span>Products added to cart by students</span>
          </div>
        </div>

        {/* Interest Indicator */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500 rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-green-700">Student Interest</p>
              {loading ? (
                <div className="h-10 w-24 bg-green-200 animate-pulse rounded mt-1"></div>
              ) : (
                <h2 className="text-4xl font-bold text-green-900 mt-1">
                  {statistics.totalCartAdditions > 0 ? 'Active' : 'Low'}
                </h2>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-green-700">
            <Package className="w-4 h-4" />
            <span>Based on cart activity</span>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500 rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-purple-700">Tracking Status</p>
              <h2 className="text-2xl font-bold text-purple-900 mt-1">
                Live
              </h2>
            </div>
          </div>
          <div className="text-sm text-purple-700">
            Real-time cart addition tracking
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">About Cart Tracking</h3>
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            <strong className="text-gray-900">📊 Purpose:</strong> This dashboard tracks student interest in educational materials before implementing the full purchase system.
          </p>
          <p>
            <strong className="text-gray-900">🔔 Notifications:</strong> You receive a notification each time a student adds a product to their cart, including the student name and product details.
          </p>
          <p>
            <strong className="text-gray-900">📈 Statistics:</strong> The total count shows how many times products have been added to carts across all students.
          </p>
          <p>
            <strong className="text-gray-900">🎯 Next Steps:</strong> Use this data to understand which products students are interested in before launching the full e-commerce functionality.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManageCarts;
